import {
  createEntityAdapter,
  createSlice,
  EntityState,
} from "@reduxjs/toolkit";
import {
  AppointingAuthAssessment,
  Assessment,
  ZonalCommitteeAssessment,
} from "../../../../../../src/api/observationByAuthority";
import { RootState } from "../../../../../../src/app/store";
import {
  fetchAssessments,
  saveAssessments,
} from "./ObservationsByAuthorityThunk";

export const isZonalCommitteeAssessment = (
  assessment: Assessment,
): assessment is ZonalCommitteeAssessment => {
  return (
    "meeting_minutes" in assessment ||
    "deliberation_from_convener" in assessment
  );
};

const appointingAuthAdapter = createEntityAdapter<
  AppointingAuthAssessment,
  string
>({
  selectId: (assessment) => assessment.assessment_id,
});

const zonalCommitteeAdapter = createEntityAdapter<
  ZonalCommitteeAssessment,
  string
>({
  selectId: (assessment) => assessment.assessment_id,
});

interface ObservationsByAuthorityState {
  appointingAuthAssessments: EntityState<AppointingAuthAssessment, string>;
  zonalCommitteeAssessments: EntityState<ZonalCommitteeAssessment, string>;
  fetchStatus: "idle" | "loading" | "succeeded" | "failed";
  fetchError: string | null;
  caseId: string | null;
}

const initialState: ObservationsByAuthorityState = {
  appointingAuthAssessments: appointingAuthAdapter.getInitialState(),
  zonalCommitteeAssessments: zonalCommitteeAdapter.getInitialState(),
  fetchStatus: "idle",
  fetchError: null,
  caseId: null,
};

const observationsByAuthoritySlice = createSlice({
  name: "observationsByAuthority",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(fetchAssessments.pending, (state) => {
      appointingAuthAdapter.removeAll(state.appointingAuthAssessments);
      zonalCommitteeAdapter.removeAll(state.zonalCommitteeAssessments);
      state.fetchStatus = "loading";
      state.fetchError = null;
    });

    builder.addCase(fetchAssessments.fulfilled, (state, action) => {
      if (action.payload.appointing_auth_assessments) {
        appointingAuthAdapter.setAll(
          state.appointingAuthAssessments,
          action.payload.appointing_auth_assessments,
        );
      }
      if (action.payload.zonal_committee_assessments) {
        zonalCommitteeAdapter.setAll(
          state.zonalCommitteeAssessments,
          action.payload.zonal_committee_assessments,
        );
      }
      state.fetchStatus = "succeeded";
      state.fetchError = null;
      state.caseId = action.meta.arg;
    });

    builder.addCase(fetchAssessments.rejected, (state, action) => {
      state.fetchStatus = "failed";
      state.fetchError = action.payload ?? "Something went wrong";
    });

    builder.addCase(saveAssessments.fulfilled, (state, action) => {
      if (action.payload.appointing_auth_assessments?.length) {
        appointingAuthAdapter.upsertMany(
          state.appointingAuthAssessments,
          action.payload.appointing_auth_assessments,
        );
      }
      if (action.payload.zonal_committee_assessments?.length) {
        zonalCommitteeAdapter.upsertMany(
          state.zonalCommitteeAssessments,
          action.payload.zonal_committee_assessments,
        );
      }

      const { payload: requestPayload } = action.meta.arg;

      if (requestPayload.update_appointing_auth_assessments?.length) {
        requestPayload.update_appointing_auth_assessments.forEach((update) => {
          appointingAuthAdapter.updateOne(state.appointingAuthAssessments, {
            id: update.assessment_id,
            changes: {
              recommendation: update.recommendation,
              borrower_or_perpatrator_kyc_assessment:
                update.borrower_or_perpatrator_kyc_assessment,
              primary_security_assessment: update.primary_security_assessment,
              collateral_security_assessment:
                update.collateral_security_assessment,
              end_use_of_funds_assessment: update.end_use_of_funds_assessment,
              business_operation_assessment:
                update.business_operation_assessment,
              io_findings_assessment: update.io_findings_assessment,
            },
          });
        });
      }

      if (requestPayload.update_zonal_committee_assessments?.length) {
        requestPayload.update_zonal_committee_assessments.forEach((update) => {
          zonalCommitteeAdapter.updateOne(state.zonalCommitteeAssessments, {
            id: update.assessment_id,
            changes: {
              recommendation: update.recommendation,
              meeting_minutes: update.meeting_minutes,
              deliberation_from_convener: update.deliberation_from_convener,
              borrower_or_perpatrator_kyc_assessment:
                update.borrower_or_perpatrator_kyc_assessment,
              primary_security_assessment: update.primary_security_assessment,
              collateral_security_assessment:
                update.collateral_security_assessment,
              end_use_of_funds_assessment: update.end_use_of_funds_assessment,
              business_operation_assessment:
                update.business_operation_assessment,
              io_findings_assessment: update.io_findings_assessment,
            },
          });
        });
      }
    });
  },
});

const appointingAuthSelectors = appointingAuthAdapter.getSelectors<RootState>(
  (state) => state.observationsByAuthority?.appointingAuthAssessments,
);

const zonalCommitteeSelectors = zonalCommitteeAdapter.getSelectors<RootState>(
  (state) => state.observationsByAuthority?.zonalCommitteeAssessments,
);

export const getAppointingAuthAssessments = appointingAuthSelectors.selectAll;
export const getZonalCommitteeAssessments = zonalCommitteeSelectors.selectAll;

export const getAppointingAuthAssessmentById =
  appointingAuthSelectors.selectById;
export const getZonalCommitteeAssessmentById =
  zonalCommitteeSelectors.selectById;

export const getAssessment = (state: RootState): Assessment | undefined => {
  const appointingAuth = getAppointingAuthAssessments(state);
  const zonalCommittee = getZonalCommitteeAssessments(state);
  return appointingAuth[0] ?? zonalCommittee[0];
};

export const getFetchStatus = (state: RootState) =>
  state.observationsByAuthority?.fetchStatus ?? "idle";

export const getFetchError = (state: RootState) =>
  state.observationsByAuthority?.fetchError ?? null;

export const getZonalCommitteeAssessmentState = (state: RootState) =>
  state.observationsByAuthority?.zonalCommitteeAssessments;

export const getAppointingAuthAssessmentState = (state: RootState) =>
  state.observationsByAuthority?.appointingAuthAssessments;

export const observationsByAuthorityReducer =
  observationsByAuthoritySlice.reducer;
